"use client";

import Link from 'next/link';
import { ThemeToggle } from '@/components/ThemeToggle';

export default function TermsPage() {
	return (
		<main className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
			<header className="w-full p-6 flex justify-between items-center">
				<Link href="/" className="text-2xl font-bold text-gradient bg-gradient-to-r from-primary-600 via-teal-500 to-primary-600 bg-clip-text text-transparent">
					Karrelo
				</Link>
				<ThemeToggle />
			</header>
			
			<div className="max-w-4xl mx-auto px-6 py-12">
				<div className="prose prose-slate dark:prose-invert max-w-none space-y-6 text-slate-700 dark:text-slate-300">
					<h1 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900 dark:text-slate-100">Användarvillkor för Karrelo</h1>
					
					<div className="mb-6 text-sm text-slate-500 dark:text-slate-400 space-y-1">
						<p><strong>Senast uppdaterad:</strong> 01 september 2025</p>
						<p><strong>Ansvarig:</strong> Karrelo - Jobb Store i Sverige AB</p>
						<p><strong>Org.nr:</strong> 559046-0282</p>
						<p><strong>Kontakt:</strong> <a href="mailto:support@karrelo.se" className="text-primary-600 dark:text-primary-400 hover:underline">support@karrelo.se</a></p>
					</div>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">1. Allmänt</h2>
						<p>
							Karrelo är en digital tjänst som hjälper användare att skapa CV, personliga brev, analysera kompetenser och få rekommendationer baserat på sin profil.
						</p>
						<p>
							Genom att skapa ett konto accepterar du dessa användarvillkor.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">2. Tjänstens omfattning</h2>
						<p>Karrelo erbjuder bland annat:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Skapande och analys av CV och personligt brev</li>
							<li>Kompetensmatchning och jobbförslag</li>
							<li>Automatiserade rekommendationer baserat på dina uppgifter</li>
							<li>Möjlighet att spara och hantera dokument i ditt konto</li>
						</ul>
						<p className="mt-3">
							Tjänsten utvecklas löpande. Nya funktioner kan tillkomma och befintliga kan ändras.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">3. Användarkonto</h2>
						<p>För att använda Karrelo behöver du registrera ett konto.</p>
						<p>Du ansvarar för att:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Lämna korrekta uppgifter</li>
							<li>Skydda ditt lösenord</li>
							<li>Inte dela ditt konto med någon annan</li>
						</ul>
						<p className="mt-3">
							Om misstänkt missbruk sker kan Karrelo tillfälligt eller permanent stänga kontot.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">4. Betalning och abonnemang</h2>
						<p>
							Karrelo erbjuder en 7 dagars kostnadsfri prova-på-period.
						</p>
						<p>
							Efter provperioden behöver du välja ett abonnemang för att fortsätta använda tjänsten.
						</p>
						<p>
							Priser visas i tjänsten innan köp genomförs.
						</p>
						<p>Du godkänner att:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Abonnemanget förlängs automatiskt om du inte avbryter innan perioden löper ut</li>
							<li>Ingen återbetalning ges för redan påbörjade abonnemangsperioder, enligt branschpraxis</li>
						</ul>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">5. Användning av AI</h2>
						<p>Karrelo använder AI-komponenter för att:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Analysera CV</li>
							<li>Generera texter</li>
							<li>Ge rekommendationer och matchning</li>
						</ul>
						<p className="mt-3">
							AI-resultat ska ses som rekommendationer, inte garantier.
						</p>
						<p>
							Du är själv ansvarig för att granska dina dokument innan du använder dem i jobbsök.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">6. Ansvar och begränsningar</h2>
						<p>Karrelo ger inga garantier för:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Att du får ett jobb</li>
							<li>Att analyser är felfria</li>
							<li>Att matchningar alltid är relevanta för alla branscher</li>
							<li>Att tjänsten är tillgänglig utan avbrott</li>
						</ul>
						<p className="mt-3">
							Vi gör däremot vårt bästa för att leverera en stabil och träffsäker tjänst.
						</p>
						<p>
							Karrelo ansvarar inte för ekonomisk skada eller följdskador som uppstår från användning av tjänsten.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">7. Tillåten användning</h2>
						<p>Du får inte:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Missbruka tjänsten eller försöka manipulera systemet</li>
							<li>Ladda upp olagligt material</li>
							<li>Använda Karrelo i strid med svensk lag eller GDPR</li>
							<li>Återpublicera eller sälja innehåll som genereras åt andra användare</li>
						</ul>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">8. Immateriella rättigheter</h2>
						<p>
							All kod, design, text, logotyper och struktur tillhör Jobb Store i Sverige AB.
						</p>
						<p>
							Du får använda tjänsten, men du får inte kopiera, återskapa eller distribuera delar av den utan skriftligt godkännande.
						</p>
						<p>
							Dokument du själv laddar upp eller skapar äger du fortsatt själv.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">9. Uppsägning och radering</h2>
						<p>Du kan när som helst:</p>
						<ul className="list-disc pl-6 space-y-2">
							<li>Avsluta ditt abonnemang</li>
							<li>Radera ditt konto</li>
						</ul>
						<p className="mt-3">
							Vi tar bort dina personuppgifter inom 30 dagar, enligt vår integritetspolicy.
						</p>
					</section>
					
					<section>
						<h2 className="text-2xl font-semibold mb-4">10. Kontakt</h2>
						<p>
							<strong>Karrelo – Jobb Store i Sverige AB</strong>
						</p>
						<p>
							E-post: <a href="mailto:support@karrelo.se" className="text-primary-600 dark:text-primary-400 hover:underline">support@karrelo.se</a>
						</p>
						<p>
							Stockholm, Sverige
						</p>
					</section>
				</div>
				
				<div className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800">
					<Link href="/" className="text-primary-600 dark:text-primary-400 hover:underline">
						← Tillbaka till startsidan
					</Link>
				</div>
			</div>
		</main>
	);
}
